import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import React from 'react';
import { apiLambda } from '../services/apiLambda';
import _ from 'lodash';
import { IDebts, IMandatoryDebt } from '../hooks/useDebts';
import { api } from '../services/api';

export type DebtType = 'ipva' | 'dpvat' | 'multas' | 'outros' | 'licenciamento';

const CUPOM_SPAN_REGEX = /(<span hidden>)(.*)(<\/span>)/;

export interface IDebtsByUf {
  uf: string;
  data: {
    placa: string;
    dpvat: number;
    ipva: number;
    licenciamento: number;
    multas: number;
    outros: number;
    total: number;
  }[];
}

export interface IConsult {
  nome_consulta: string;
  campos: {
    descricao: string;
    conteudo: string;
  }[];
  debitos: {
    ipva: IConsultDebt[];
    licenciamento: IConsultDebt[];
    dpvat: IConsultDebt[];
    multas: IConsultDebt[];
    outros: IConsultDebt[];
  };
  pagarTudo: boolean;
  mensagemEntrada: string;
  mensagemSaida: string;
  codigo: number;
}

export interface IConsultDebt {
  codigo: number;
  descricao: string;
  descricaoAdicional: string;
  emissor: string;
  expired: boolean;
  grupo: number;
  validade: string;
  valor: number;
  vencimento: string;
  disable?: boolean;
  ano?: number;
  unica?: boolean;
  quantidadeAno?: number;
  onCheck: {
    select: number[];
    unSelect: number[];
  };
  onUnCheck: {
    select: number[];
    unSelect: number[];
  };
}

interface PaymentContextData {
  handleSetCupom(cupom: string): void;
  cupomStatus: boolean;
  cupom: string;
  entryMessage: string;
  exitMessage: string;
  installmentsLoading: boolean;
  availableCoupon: string;
  selectedDebts: IDebts[];
  allDebtsSelected: IAllDebts[];
  setSelectedDebts: (item: IDebts[]) => void;
  handleSelectInstallment(installment: InstallmentInfo): void;
  handleSelectPix(): void;
  installments: InstallmentInfo[];
  pix: InstallmentInfo | undefined;
  debtsForTable: IDebtsByUf[];
  handleGetConsult: () => void;
  isPix: boolean;
  ipvaTotal: number;
  licenciamentoTotal: number;
  dpvatTotal: number;
  multasTotal: number;
  outrosTotal: number;
  debtsTotal: number;
  consultCode: number | undefined;
  handleDebtsByType: (type: "multas" | "licenciamento" | "ipva" | "dpvat" | "outros", debts: IMandatoryDebt[] | IDebts[] | IAllDebts[]) => (IDebts | IMandatoryDebt | IAllDebts | any)[]
  handleConsultClean: (keepDebts?: boolean) => void;
}

export interface IAllDebts {
  debtsCode: number;
  value: number;
  type: string;
  plate: string;
  uf: string;
  consultName: string;
  expirationDate: string;
  description: string;
  additionalDescription: string;
}

export interface InstallmentInfo {
  month: number;
  installmentPrice: number;
  totalPrice: number;
  checked: boolean;
  discount: number;
  cet: number;
}

interface IResponseInstallments {
  tipo: string;
  parcelas: number;
  cet: number;
  valor: number;
  total: number;
  desconto: number;
}

const PaymentContext = createContext<PaymentContextData>(
  {} as PaymentContextData,
);

export const PaymentProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [ipvaTotal, setIpvaTotal] = useState(0);
  const [licenciamentoTotal, setLicenciamentoTotal] = useState(0);
  const [dpvatTotal, setDpvatTotal] = useState(0);
  const [multasTotal, setMultasTotal] = useState(0);
  const [outrosTotal, setOutrosTotal] = useState(0);
  const [debtsTotal, setDebtsTotal] = useState(0);

  const [cupom, setCupom] = useState('');
  const [cupomStatus, setCupomStatus] = useState(false);
  const [availableCoupon, setAvaliableCoupon] = useState('');

  const [ready, setReady] = useState<boolean>(false);

  const [entryMessage, setEntryMessage] = useState('');
  const [exitMessage, setExitMessage] = useState('');
  const [installments, setInstallments] = useState<InstallmentInfo[]>([]);
  const [pix, setPix] = useState<InstallmentInfo | undefined>(undefined);
  const [isPix, setIsPix] = useState<boolean>(false);
  const [installmentsLoading, setLoading] = useState(false);
  const [consultCode, setConsultCode] = useState<number | undefined>();
  const [consult, setConsult] = useState<IConsult | undefined>();

  const [selectedDebts, setSelectedDebts] = useState<IDebts[]>([]);
  const [debtsForTable, setDebtsForTable] = useState<IDebtsByUf[]>([]);
  const [allDebtsSelected, setAllDebtsSelected] = useState<IAllDebts[]>([]);


  function handleResponseInstallmentsToInstallments(
    installments: IResponseInstallments[],
  ) {
    let checkedPix = false;
    let parcela = 12;

    const pix = JSON.parse(localStorage.getItem('@Zignet:pix')!);

    if (pix) {
      checkedPix = pix?.checked;
      setIsPix(checkedPix);
    }

    const parcelas = JSON.parse(localStorage.getItem('@Zignet:parcelas')!);

    if (parcelas) {
      parcela = parcelas.find((p: any) => p?.checked)?.month;
    }

    const newInstallments: InstallmentInfo[] = installments
      .filter((i) => i?.tipo === 'C')
      .filter((i, n) => i?.parcelas <= 12)
      .map((i) => ({
        month: i.parcelas,
        installmentPrice: i.valor,
        totalPrice: i.total,
        discount: i.desconto,
        checked: checkedPix ? false : i.parcelas === parcela ? true : false,
        cet: i.cet,
      }))
      .sort((a, b) => {
        return a.month < b.month ? 1 : -1;
      });

    const pixInstallments: InstallmentInfo[] = installments
      ?.filter((i) => i.tipo === 'P')
      ?.map((i) => ({
        month: i?.parcelas,
        installmentPrice: i?.valor,
        totalPrice: i?.total,
        discount: i?.desconto,
        checked: checkedPix ? true : false,
        cet: i?.cet,
      }));

    setInstallments(newInstallments);
    localStorage.setItem('@Zignet:parcelas', JSON.stringify(newInstallments));

    if (pixInstallments.length > 0) {
      setPix(pixInstallments?.[0] ?? undefined);
      localStorage.setItem('@Zignet:pix', JSON.stringify(pixInstallments?.[0]));
    }
  }

  function sumDebtsByTypeAndPlate(
    type: string,
    plate: string,
    allDebts: IAllDebts[],
  ) {
    return allDebts
      .filter((debt) => debt.plate === plate && debt.type === type)
      .reduce((acc, debt) => {
        return acc + debt.value;
      }, 0);
  }

  function sumDebts(data: { [key: string]: number | string }[], key: string) {
    if (data && data.length > 0 && key in data[0]) {
      return data.reduce((acc, debts) => {
        return acc + Number(debts[key] ?? 0);
      }, 0);
    }
    return 0
  }

  function sumAllRowsColumns(data: IDebtsByUf[]) {
    const sumOfDebtsByUf = data.map(debts => ({
      uf: debts.uf,
      ipva: sumDebts(debts?.data, "ipva"),
      licenciamento: sumDebts(debts?.data, "licenciamento"),
      multas: sumDebts(debts?.data, "multas"),
      dpvat: sumDebts(debts?.data, "dpvat"),
      outros: sumDebts(debts?.data, "outros"),
      total: sumDebts(debts?.data, "total")
    }))

    setIpvaTotal(sumDebts(sumOfDebtsByUf, "ipva"));
    setDpvatTotal((sumDebts(sumOfDebtsByUf, "dpvat")));
    setMultasTotal((sumDebts(sumOfDebtsByUf, "multas")));
    setLicenciamentoTotal((sumDebts(sumOfDebtsByUf, "licenciamento")));
    setOutrosTotal((sumDebts(sumOfDebtsByUf, "outros")));
    setDebtsTotal((sumDebts(sumOfDebtsByUf, "total")));
  }

  useEffect(() => {
    const allDebts: IAllDebts[] = [];
    selectedDebts.forEach((debt) => {
      if (debt.onCheck && debt.onCheck.select) {
        debt.onCheck.select.forEach((mandatoryDebt) => {
          if (
            !allDebts
              .map((debt) => debt.debtsCode)
              .includes(mandatoryDebt.debtsCode)
          ) {
            allDebts.push({
              debtsCode: mandatoryDebt.debtsCode,
              value: mandatoryDebt.price,
              type: mandatoryDebt.type,
              plate: debt.vehiclePlate,
              uf: debt.vehicleUF,
              additionalDescription: debt.additionalDescription ?? "",
              consultName: debt.consultName,
              expirationDate: debt.expirationDate,
              description: debt.description
            });
          }
        });
      }

      if (!allDebts.map((debt) => debt.debtsCode).includes(debt.debtsCode)) {
        allDebts.push({
          debtsCode: debt.debtsCode,
          value: debt.price,
          type: debt.type,
          plate: debt.vehiclePlate,
          uf: debt.vehicleUF,
          additionalDescription: debt.additionalDescription ?? "",
          consultName: debt.consultName,
          expirationDate: debt.expirationDate,
          description: debt.description
        });
      }
    });

    setAllDebtsSelected(allDebts)

    const allUfs: string[] = [];
    selectedDebts.forEach((debt) => {
      if (!allUfs.includes(debt.vehicleUF)) {
        allUfs.push(debt.vehicleUF);
      }
    });

    const allDebtsByUf = allUfs.map((uf) => {
      const debtsByUf = allDebts.filter((debt) => uf === debt.uf);

      const allPlates: string[] = [];
      debtsByUf.forEach((debt) => {
        if (!allPlates.includes(debt.plate)) {
          allPlates.push(debt.plate);
        }
      });

      const allTypes: string[] = [];
      debtsByUf.forEach((debt) => {
        if (!allTypes.includes(debt.type)) {
          allTypes.push(debt.type);
        }
      });

      const data = allPlates.map((plate) => {
        const dpvat = allTypes.includes('dpvat')
          ? sumDebtsByTypeAndPlate('dpvat', plate, debtsByUf)
          : 0;
        const ipva = allTypes.includes('ipva')
          ? sumDebtsByTypeAndPlate('ipva', plate, debtsByUf)
          : 0;
        const licenciamento = allTypes.includes('licenciamento')
          ? sumDebtsByTypeAndPlate('licenciamento', plate, debtsByUf)
          : 0;
        const outros = allTypes.includes('outros')
          ? sumDebtsByTypeAndPlate('outros', plate, debtsByUf)
          : 0;
        const multas = allTypes.includes('multas')
          ? sumDebtsByTypeAndPlate('multas', plate, debtsByUf)
          : 0;

        const total = dpvat + ipva + licenciamento + outros + multas;

        return {
          placa: plate,
          dpvat,
          ipva,
          licenciamento,
          multas,
          outros,
          total,
        };
      });

      return {
        uf,
        data,
      };
    });


    sumAllRowsColumns(allDebtsByUf)
    setDebtsForTable(allDebtsByUf);
  }, [selectedDebts]);

  useEffect(() => {
    setLoading(true);
    const cupomStatusOnStorage = localStorage.getItem('@Zignet:cupomStatus');
    const cupomOnStorage = localStorage.getItem('@Zignet:cupom');
    const consultCodeOnStorage = localStorage.getItem('@Zignet:consultCode');
    const selectedDebtsOnStorage = localStorage.getItem('@Zignet:selectedDebts');
    const installmentsOnStorage = localStorage.getItem('@Zignet:parcelas');
    const pixOnStorage = localStorage.getItem('@Zignet:pix');

    const cupomStatusFromStorage: boolean =
      cupomStatusOnStorage && cupomStatusOnStorage !== 'undefined'
        ? JSON.parse(cupomStatusOnStorage)
        : false;

    const consultCodeFromStorage: number | undefined =
      consultCodeOnStorage && consultCodeOnStorage !== 'undefined'
        ? JSON.parse(consultCodeOnStorage)
        : undefined;

    const cupomFromStorage: string = cupomOnStorage
      ? JSON.parse(cupomOnStorage)
      : '';

    const selectedDebtsFromStorage: IDebts[] | undefined =
      selectedDebtsOnStorage && selectedDebtsOnStorage !== 'undefined'
        ? JSON.parse(selectedDebtsOnStorage)
        : undefined;

    const installmentsFromStorage: InstallmentInfo[] = installmentsOnStorage
      ? JSON.parse(installmentsOnStorage)
      : [];

    const pixFromStorage: InstallmentInfo = pixOnStorage
      ? JSON.parse(pixOnStorage)
      : {};

    setCupomStatus(cupomStatusFromStorage ?? false);
    setCupom(cupomFromStorage);
    setInstallments(installmentsFromStorage);
    setConsultCode(consultCodeFromStorage);
    setSelectedDebts(selectedDebtsFromStorage ?? []);
    setPix(pixFromStorage);

    if (pixFromStorage?.checked) {
      setIsPix(true);
    }

    setLoading(false)
  }, []);

  useEffect(() => {
    localStorage.setItem('@Zignet:cupom', JSON.stringify(cupom));
    localStorage.setItem('@Zignet:cupomStatus', JSON.stringify(cupomStatus));
    if (consultCode) localStorage.setItem('@Zignet:consultCode', JSON.stringify(consultCode));
  }, [
    cupom,
    cupomStatus,
    consultCode,
  ]);

  useEffect(() => {
    if (selectedDebts && selectedDebts.length > 0) {
      localStorage.setItem('@Zignet:selectedDebts', JSON.stringify(selectedDebts));
    }
  }, [
    selectedDebts,
  ]);

  const handleSetConsultData = useCallback((consult: IConsult) => {
    if (consult.campos.length > 0 && consult.campos[0].conteudo === 'BBR5611') {
      consult.mensagemEntrada = `<p style="width: 100%;/* margin: 0 auto; */display: flex;align-items: center;justify-content: center;"><img src="https://blog.zignet.com.br/wp-content/uploads/2023/08/MG15.png"; width="320" border="0" alt="Cupom de desconto" /></p><span hidden>ZIGMG15</span>`;
    }

    if (consult.mensagemEntrada) {
      const groups = CUPOM_SPAN_REGEX.exec(consult.mensagemEntrada);
      if (groups) {
        setAvaliableCoupon(groups[2].trim());
      }
    }

    const parser = new DOMParser();
    const doc =
      consult?.mensagemEntrada &&
      parser.parseFromString(consult?.mensagemEntrada, 'text/html');

    let generalElement = null;
    // let partnerElement = null;
    let zignetElement = null;
    if (doc) {
      generalElement =
        doc?.getElementById('general') || doc?.getElementById('GENERAL');
      // partnerElement =
      //   doc?.getElementById('partner') || doc?.getElementById('PARTNER');
      zignetElement =
        doc?.getElementById('zignet') || doc?.getElementById('ZIGNET');
    }

    if (zignetElement) {
      setEntryMessage(zignetElement.innerHTML);
    } else if (generalElement) {
      setEntryMessage(generalElement.innerHTML);
    } else if (
      !generalElement &&
      // !partnerElement &&
      !zignetElement &&
      consult?.mensagemEntrada
    ) {
      setEntryMessage(consult?.mensagemEntrada);
    }

    setExitMessage(consult?.mensagemSaida);
  }, [CUPOM_SPAN_REGEX])

  const handleGetConsult = useCallback(async () => {
    try {
      setLoading(true)
      const { data }: { data: IConsult } = await api.post(
        `/batch-payments/consults`,
        selectedDebts,
        {
          headers: {
            tokenTerminal: process.env.REACT_APP_TERMINAL
          }
        }
      )

      if (!data) {
        setInstallments([]);
        setLoading(false);
        handleConsultClean()
        return;
      }

      setConsultCode(data.codigo)
      setConsult(data)

      handleSetConsultData(data)
      setReady(true)
    } catch (error) {
    } finally {
      setLoading(false)
    }

  }, [selectedDebts])

  const handleGetInstallments = useCallback(async () => {
    try {
      setLoading(true);
      if (debtsTotal === 0) {
        setInstallments([]);
        setLoading(false);
        return;
      }

      const response = await apiLambda.get(
        `/comunix/installments/${consultCode}`,
        {
          headers: {
            terminal: process.env.REACT_APP_TERMINAL
          },
          params: {
            valor: debtsTotal,
            cupom,
          },
        },
      );

      const cumpomStatusLSStr = localStorage.getItem('@Zignet:cupomStatus') ?? '';

      const cupomStatusLocalStorage = JSON.parse(
        !cumpomStatusLSStr || cumpomStatusLSStr === 'undefined'
          ? 'false'
          : cumpomStatusLSStr,
      );

      if (cupomStatusLocalStorage) {
        setCupomStatus(cupomStatusLocalStorage);
      }

      setCupomStatus(response?.data?.data?.cupomAplicado);

      if (cupom && !response?.data?.data?.cupomAplicado) {
        setCupom('');
        return;
      }

      handleResponseInstallmentsToInstallments(response.data.data.parcelas);

    } catch (error) {
    } finally {
      setLoading(false)
      setReady(false)
    }

  }, [debtsTotal, cupom, consult, consultCode, selectedDebts]);

  const handleSelectInstallment = useCallback(
    (installment: InstallmentInfo) => {
      const newInstallments = installments.map((i) => ({
        ...i,
        checked: i.month === installment.month ? true : false,
      }));
      setInstallments(newInstallments);
      localStorage.setItem('@Zignet:parcelas', JSON.stringify(newInstallments));

      if (!_.isEmpty(pix)) {
        const newPix = {
          ...pix,
          checked: false,
        };
        setPix(newPix);
        localStorage.setItem('@Zignet:pix', JSON.stringify(newPix));
      }
      isPix && setIsPix(false);
    },
    [installments, pix, isPix],
  );

  const handleSelectPix = useCallback(() => {
    const newPix = {
      ...pix,
      checked: true,
    };
    setPix(newPix as InstallmentInfo);
    localStorage.setItem('@Zignet:pix', JSON.stringify(newPix));

    const newInstallments = installments.map((i) => ({
      ...i,
      checked: false,
    }));
    setInstallments(newInstallments);
    localStorage.setItem('@Zignet:parcelas', JSON.stringify(newInstallments));
    !isPix && setIsPix(true);
  }, [installments, pix, isPix]);

  const handleSetCupom = useCallback((cupom: string) => {
    setCupom(cupom);
    setReady(true);
  }, []);

  const handleConsultClean = useCallback((keepDebts?: boolean) => {
    localStorage.removeItem('@Zignet:cupomStatus');
    localStorage.removeItem('@Zignet:cupom');
    localStorage.removeItem('@Zignet:consultCode');
    if (!keepDebts) {
      localStorage.removeItem('@Zignet:selectedDebts');
      localStorage.removeItem('@Zignet:debtsForTable');
      localStorage.removeItem('@Zignet:allDebtsSelected');
    }
    localStorage.removeItem('@Zignet:parcelas');
    localStorage.removeItem('@Zignet:pix');
    setIsPix(false)
    setConsult(undefined)
    setConsultCode(undefined)
  }, [])

  function handleDebtsByType(type: "multas" | "licenciamento" | "ipva" | "dpvat" | "outros", debts: IMandatoryDebt[] | IDebts[] | IAllDebts[] | any[]) {
    return debts.filter(debt => debt.type === type);
  }

  useEffect(() => {
    if (ready) {
      handleGetInstallments()
    }
  }, [ready])

  return (
    <PaymentContext.Provider
      value={{
        installmentsLoading,
        handleSetCupom,
        cupomStatus,
        entryMessage,
        exitMessage,
        cupom,
        availableCoupon,
        setSelectedDebts,
        selectedDebts,
        debtsForTable,
        handleGetConsult,
        handleSelectInstallment,
        handleSelectPix,
        installments,
        pix,
        isPix,
        ipvaTotal,
        licenciamentoTotal,
        dpvatTotal,
        multasTotal,
        outrosTotal,
        debtsTotal,
        allDebtsSelected,
        consultCode,
        handleDebtsByType,
        handleConsultClean
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};

export function usePayment(): PaymentContextData {
  const context = useContext(PaymentContext);

  if (!context) {
    throw new Error('usePayment must be used within an PaymentProvider');
  }

  return context;
}
